import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useRouter } from "@/routing/navigation";
import { buildStudyCreationHandoff } from "@/pages/HomeLabPage/useHomeLabSubmit";
import { useTwinChatEnabled } from "@/hooks/useTwinChatEnabled";
import { useCreateStudyEnabled } from "@/hooks/useCreateStudyEnabled";
import { Shuffle } from "@digital-twins/design-system/icons";
import useSWR from "swr";
import { populationsApi, chatRoomsApi, usersApi, type AudienceSummary, ChatImage, type TargetingPreferences } from "@/lib/api";
import { useTheme } from "@/lib/theme-provider";
import { cn } from "@/lib/utils";
import { useFeaturedTwins } from "@/lib/hooks/use-featured-twins";
import { DisplayFontsLoader } from "@/components/shared/DisplayFontsLoader";
import { TwinCoverFlow } from "./TwinCoverFlow";
import { AudienceCoverFlow } from "./AudienceCoverFlow";
import { HomeModeToggle } from "./HomeModeToggle";
import { HomeUtilityChip } from "./HomeUtilityChip";
import { HomeComposer } from "./HomeComposer";
import { TrialLimitUpgradeModal } from "@/components/billing/TrialLimitUpgradeModal";
import { detectTrialLimitError } from "@/lib/billing/trial-limits";
import { useTrialChatRoomLimit } from "@/lib/hooks/use-trial-limits";
import { Heading } from "@/components/ui/Heading";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { HomeComposerOptionsBar, HomeChatType } from "./HomeComposerOptionsBar";
import { getChatPlaceholders } from "@/lib/constants/languages";
import { useOnboarding } from "@/components/onboarding";
import {
  fetchAllReadyAudiences,
  HOME_STUDY_AUDIENCE_LIMIT,
  selectBookmarkedAudiencesForHomepage,
} from "@/lib/homepage-personalization";

export function HomeStage() {
  const router = useRouter();
  const twinChatEnabled = useTwinChatEnabled();
  const createStudyEnabled = useCreateStudyEnabled();
  const { resolvedTheme } = useTheme();
  // structural only: drives the HomeUtilityChip `variant` prop (a discrete child
  // component visual API, not a flat color ternary). All flat color ternaries —
  // gradients, image blur saturate, composer shell, options-bar pills/stepper —
  // now flow through semantic tokens (see --home-* in globals.css).
  const isDark = resolvedTheme === "dark"; // structural

  const [mode, setMode] = useState<"chat" | "study">(twinChatEnabled ? "chat" : "study");
  const [chatActiveIndex, setChatActiveIndex] = useState(0);
  const [studyActiveIndex, setStudyActiveIndex] = useState(0);
  const [chatMessage, setChatMessage] = useState("");
  const [studyMessage, setStudyMessage] = useState("");
  const [chatType, setChatType] = useState<HomeChatType>('interview')
  const [participantCount, setParticipantCount] = useState(3)
  const [isCreatingRoom, setIsCreatingRoom] = useState(false)
  const [trialRoomLimitOpen, setTrialRoomLimitOpen] = useState(false)
  const { limitReached: trialRoomLimitReached } = useTrialChatRoomLimit()
  const [language, setLanguage] = useState('en')
  useEffect(() => {
    if (!twinChatEnabled && mode === 'chat') {
      setMode('study')
    }
    if (!createStudyEnabled && mode === 'study') {
      setMode('chat')
    }
  }, [twinChatEnabled, createStudyEnabled, mode])
  const [pendingImages, setPendingImages] = useState<Array<ChatImage>>([])
  const [pendingPdfText, setPendingPdfText] = useState<string | null>(null)
  const [pendingPdfName, setPendingPdfName] = useState<string | null>(null)
  const [previousBackgroundUrl, setPreviousBackgroundUrl] = useState<string | null>(null);
  const [currentBackgroundUrl, setCurrentBackgroundUrl] = useState<string | null>(null);
  /** Same-mode crossfade: after rAF, fade previous out and current in (snapped on mode change). */
  const [fadeBackgroundToIncoming, setFadeBackgroundToIncoming] = useState(false);
  const currentBackgroundUrlRef = useRef<string | null>(null);
  const modeRef = useRef(mode);
  const onboarding = useOnboarding();
  const canLoadHomePersonalization = !onboarding.isLoading && !onboarding.showWelcome;

  const { twins, isLoading: twinsLoading } = useFeaturedTwins({ enabled: canLoadHomePersonalization });

  const { data: audiences = [], isLoading: audiencesLoading } = useSWR<
    AudienceSummary[]
  >(
    canLoadHomePersonalization ? "home-audiences" : null,
    fetchAllReadyAudiences,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  const { data: targeting, isLoading: targetingLoading } = useSWR<TargetingPreferences | null>(
    canLoadHomePersonalization ? "home-targeting-preferences" : null,
    () => usersApi.getTargetingPreferences().catch(() => null),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  const studyAudiences = useMemo(() => {
    return selectBookmarkedAudiencesForHomepage(audiences, targeting, HOME_STUDY_AUDIENCE_LIMIT)
  }, [audiences, targeting]);
  const studyAudiencesLoading = !canLoadHomePersonalization || audiencesLoading || targetingLoading;

  useEffect(() => {
    if (twins.length === 0) {
      setChatActiveIndex(0);
      return;
    }
    if (chatActiveIndex > twins.length - 1) setChatActiveIndex(0);
  }, [twins.length, chatActiveIndex]);

  useEffect(() => {
    if (studyAudiences.length === 0) {
      setStudyActiveIndex(0);
      return;
    }
    if (studyActiveIndex > studyAudiences.length - 1) setStudyActiveIndex(0);
  }, [studyAudiences.length, studyActiveIndex]);

  const activeTwin = useMemo(() => {
    if (twins.length === 0) return null;
    return twins[chatActiveIndex] ?? twins[0];
  }, [twins, chatActiveIndex]);

  const activeAudience = useMemo(() => {
    if (studyAudiences.length === 0) return null;
    return studyAudiences[studyActiveIndex % studyAudiences.length];
  }, [studyAudiences, studyActiveIndex]);

  const clearPendingFiles = useCallback(() => {
    setPendingImages([])
    setPendingPdfText(null)
    setPendingPdfName(null)
  }, [])

  const handleStartChat = async () => {
    const value = chatMessage.trim();
    const populationId = activeTwin?.populationId;
    const personaId = activeTwin?.personaId;
    const isValidId = (id: unknown): id is string =>
      typeof id === "string" &&
      id.length > 0 &&
      id !== "null" &&
      id !== "undefined";
    if (!isValidId(populationId) || !isValidId(personaId)) return;
    if (isCreatingRoom) return;
    if (trialRoomLimitReached) {
      setTrialRoomLimitOpen(true);
      return;
    }

    if (pendingImages.length > 0 || pendingPdfText) {
      sessionStorage.setItem('home-pending-files', JSON.stringify({
        images: pendingImages,
        pdfText: pendingPdfText,
        pdfName: pendingPdfName,
        timestamp: Date.now(),
      }))
    }

    if (!twinChatEnabled) {
      return;
    }
    setIsCreatingRoom(true);
    try {
      let participantPersonaIds: Array<string> = [personaId];

      if (chatType === 'group') {
        const extraNeeded = Math.max(0, participantCount - 1);
        if (extraNeeded > 0) {
          const { personaIds: randomIds } = await populationsApi.getRandomPersonaIds(
            populationId,
            extraNeeded,
            undefined,
            [personaId],
          );
          participantPersonaIds = [personaId, ...randomIds];
        }
      }

      const room = await chatRoomsApi.create({
        populationId,
        participantPersonaIds,
        settings: {
          language,
          ...(value ? { topics: value } : {}),
        },
      });

      track(AnalyticsEvents.HOME_ROOM_QUICKSTART_CREATED, {
        audience_id: populationId,
        invite_count: chatType === 'group' ? participantCount - 1 : 0,
        language,
      });

      router.push(`/chats/${room.token}`, {
        state: {
          initialMessage: value || undefined,
          hasPendingFiles: pendingImages.length > 0 || !!pendingPdfText || undefined,
        },
      });
      clearPendingFiles();
    } catch (err) {
      // Trial room cap reached — show the upgrade modal before the chat starts.
      if (detectTrialLimitError(err) === 'rooms') {
        setTrialRoomLimitOpen(true);
        setIsCreatingRoom(false);
        return;
      }
      console.error('Failed to start chat from homepage:', err);
      setIsCreatingRoom(false);
    }
  };

  const handleStudySubmit = () => {
    if (!createStudyEnabled) return;
    if (!activeAudience?.id) return;
    track(AnalyticsEvents.HOME_STUDY_FLOW_STARTED, {
      audience_id: activeAudience.id,
      has_message: studyMessage.trim().length > 0,
    });
    const handoff = buildStudyCreationHandoff({
      audienceId: activeAudience.id,
      message: studyMessage,
      studyTypeId: null,
      language,
    });
    router.push(handoff.href, { state: handoff.state });
  };

  const handleChatShuffle = useCallback(() => {
    if (twins.length <= 1) return;
    let next: number;
    do {
      next = Math.floor(Math.random() * twins.length);
    } while (next === chatActiveIndex && twins.length > 1);
    setChatActiveIndex(next);
  }, [twins.length, chatActiveIndex]);

  const handleStudyShuffle = useCallback(() => {
    if (studyAudiences.length <= 1) return;
    let next: number;
    do {
      next = Math.floor(Math.random() * studyAudiences.length);
    } while (next === studyActiveIndex && studyAudiences.length > 1);
    setStudyActiveIndex(next);
  }, [studyAudiences.length, studyActiveIndex]);

  const BACKGROUND_FALLBACK = "/images/twin-fallback.png";
  const backgroundImageUrl = useMemo(() => {
    if (mode === "chat") {
      return activeTwin?.imageUrl || (activeTwin ? BACKGROUND_FALLBACK : null);
    }
    if (mode === "study" && activeAudience?.coverImageUrl) {
      return activeAudience.coverImageUrl;
    }
    return null;
  }, [mode, activeTwin, activeAudience?.coverImageUrl]);

  useEffect(() => {
    const modeChanged = modeRef.current !== mode;

    if (!backgroundImageUrl) {
      setCurrentBackgroundUrl(null);
      setPreviousBackgroundUrl(null);
      setFadeBackgroundToIncoming(false);
      currentBackgroundUrlRef.current = null;
      modeRef.current = mode;
      return;
    }

    const previous = currentBackgroundUrlRef.current;

    if (previous === backgroundImageUrl) {
      modeRef.current = mode;
      return;
    }

    if (modeChanged) {
      currentBackgroundUrlRef.current = backgroundImageUrl;
      setPreviousBackgroundUrl(null);
      setCurrentBackgroundUrl(backgroundImageUrl);
      setFadeBackgroundToIncoming(false);
      modeRef.current = mode;
      return;
    }

    if (previous == null) {
      currentBackgroundUrlRef.current = backgroundImageUrl;
      setPreviousBackgroundUrl(null);
      setCurrentBackgroundUrl(backgroundImageUrl);
      setFadeBackgroundToIncoming(false);
      modeRef.current = mode;
      return;
    }

    currentBackgroundUrlRef.current = backgroundImageUrl;
    setPreviousBackgroundUrl(previous);
    setCurrentBackgroundUrl(backgroundImageUrl);
    setFadeBackgroundToIncoming(false);

    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setFadeBackgroundToIncoming(true);
      });
    });

    modeRef.current = mode;
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [backgroundImageUrl, mode]);

  const hasBackground =
    currentBackgroundUrl != null || previousBackgroundUrl != null;

  const backgroundOverlayStyle = useMemo(
    () => ({ background: 'var(--home-overlay-gradient)' }),
    [],
  );

  const emptyBackgroundStyle = useMemo(
    () => ({ background: 'var(--home-empty-gradient)' }),
    [],
  );

  const activeTwinAudience = useMemo(() => {
    if (!activeTwin?.populationId) return undefined
    return audiences.find((a) => a.id === activeTwin.populationId)
  }, [activeTwin, audiences])
  const activeTwinTier = activeTwinAudience?.tier

  const chatFooter: ReactNode = useMemo(() => (
    <HomeComposerOptionsBar
      chatType={chatType}
      onChatTypeChange={setChatType}
      participantCount={participantCount}
      onParticipantCountChange={setParticipantCount}
      language={language}
      onLanguageChange={setLanguage}
      images={pendingImages}
      onImagesChange={setPendingImages}
      pdfText={pendingPdfText}
      pdfName={pendingPdfName}
      onPdfChange={(text, name) => { setPendingPdfText(text); setPendingPdfName(name) }}
      audienceTier={activeTwinTier}
      chatCreditsPerMessage={activeTwinAudience?.chatCreditsPerMessage}
      focusGroupCreditsPerMessage={activeTwinAudience?.focusGroupCreditsPerMessage}
    />
  ), [chatType, participantCount, language, pendingImages, pendingPdfText, pendingPdfName, activeTwinTier, activeTwinAudience])

  return (
    <section className="relative flex w-full bg-background">
      <DisplayFontsLoader />
      <TrialLimitUpgradeModal
        open={trialRoomLimitOpen}
        onClose={() => setTrialRoomLimitOpen(false)}
        kind="rooms"
      />
      {hasBackground ? (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[1000px] z-0 overflow-hidden"
          style={{
            WebkitMaskImage: [
              "linear-gradient(to right, transparent, black 28%, black 72%, transparent)",
              "linear-gradient(to bottom, transparent, black 22%, black 78%, transparent)",
            ].join(", "),
            WebkitMaskComposite: "destination-in",
            maskImage: [
              "linear-gradient(to right, transparent, black 28%, black 72%, transparent)",
              "linear-gradient(to bottom, transparent, black 22%, black 78%, transparent)",
            ].join(", "),
            maskComposite: "intersect",
          }}
        >
          {previousBackgroundUrl ? (
            <img
              src={previousBackgroundUrl}
              alt=""
              className={cn(
                "absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ease-out",
                fadeBackgroundToIncoming ? "opacity-0" : "opacity-100",
              )}
              style={{
                filter: 'blur(180px) saturate(var(--home-image-saturate))',
                transform: "scale(1.3)",
              }}
              aria-hidden="true"
            />
          ) : null}

          {currentBackgroundUrl ? (
            <img
              src={currentBackgroundUrl}
              alt=""
              className={cn(
                "absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ease-out",
                previousBackgroundUrl != null && !fadeBackgroundToIncoming
                  ? "opacity-0"
                  : "opacity-100",
              )}
              style={{
                filter: 'blur(180px) saturate(var(--home-image-saturate))',
                transform: "scale(1.3)",
              }}
              aria-hidden="true"
            />
          ) : null}

          <div
            className="absolute inset-0"
            style={backgroundOverlayStyle}
            aria-hidden="true"
          />
        </div>
      ) : (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[1000px] z-0"
          style={{
            ...emptyBackgroundStyle,
            WebkitMaskImage: [
              "linear-gradient(to right, transparent, black 28%, black 72%, transparent)",
              "linear-gradient(to bottom, transparent, black 22%, black 78%, transparent)",
            ].join(", "),
            WebkitMaskComposite: "destination-in",
            maskImage: [
              "linear-gradient(to right, transparent, black 28%, black 72%, transparent)",
              "linear-gradient(to bottom, transparent, black 22%, black 78%, transparent)",
            ].join(", "),
            maskComposite: "intersect",
          }}
        />
      )}

      <div className="relative z-10 mx-auto flex min-h-dvh md:min-h-screen w-full items-center justify-center px-6 py-10 md:px-10">
        <div className="flex w-full max-w-[1215px] flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-5">
            <Heading
              level={1}
              weight="normal"
              className={cn(
                "text-center font-display text-[2rem] md:text-[40px] leading-[1.2] tracking-[-0.8px]",
                "text-[hsl(var(--home-heading-fg))]",
              )}
            >
              Start with a question.
              <br />
              Leave with clarity.
            </Heading>

            {twinChatEnabled && createStudyEnabled ? (
              <HomeModeToggle value={mode} onChange={setMode} />
            ) : null}
          </div>

          <div className="relative mt-4 md:mt-2 w-full">
            {mode === "chat" && twins.length > 1 ? (
              <div className="hidden md:block absolute right-0 top-0 z-30">
                <HomeUtilityChip
                  icon={Shuffle}
                  label="Shuffle"
                  onClick={handleChatShuffle}
                  variant={isDark ? "dark" : "light"} // structural: child component variant API
                />
              </div>
            ) : null}

            {mode === "study" && studyAudiences.length > 1 ? (
              <div className="hidden md:block absolute right-0 top-0 z-30">
                <HomeUtilityChip
                  icon={Shuffle}
                  label="Shuffle"
                  onClick={handleStudyShuffle}
                  variant={isDark ? "dark" : "light"} // structural: child component variant API
                />
              </div>
            ) : null}

            {mode === "chat" ? (
              <TwinCoverFlow
                twins={twins}
                isLoading={twinsLoading}
                activeIndex={chatActiveIndex}
                onActiveIndexChange={setChatActiveIndex}
              />
            ) : (
              <AudienceCoverFlow
                audiences={studyAudiences}
                isLoading={studyAudiencesLoading}
                activeIndex={studyActiveIndex}
                onActiveIndexChange={setStudyActiveIndex}
              />
            )}
          </div>

          {mode === "chat" && twins.length > 1 ? (
            <div className="flex md:hidden justify-center -mt-[60px]">
              <HomeUtilityChip
                icon={Shuffle}
                label="Shuffle"
                onClick={handleChatShuffle}
                variant={isDark ? "dark" : "light"} // structural: child component variant API
              />
            </div>
          ) : null}

          {mode === "study" && studyAudiences.length > 1 ? (
            <div className="flex md:hidden justify-center -mt-[60px]">
              <HomeUtilityChip
                icon={Shuffle}
                label="Shuffle"
                onClick={handleStudyShuffle}
                variant={isDark ? "dark" : "light"} // structural: child component variant API
              />
            </div>
          ) : null}

          <div className="flex w-full max-w-4xl justify-center mt-4 md:mt-0">
            {mode === "chat" ? (
              <HomeComposer
                value={chatMessage}
                onValueChange={setChatMessage}
                onSubmit={handleStartChat}
                placeholder={(() => {
                  const t = getChatPlaceholders(language)
                  if (chatType === 'group') {
                    return activeTwin?.personaName ? t.group(activeTwin.personaName) : t.groupFallback
                  }
                  return activeTwin?.personaName ? t.interview(activeTwin.personaName) : t.interviewFallback
                })()}
                submitLabel={chatType === 'group' ? "Set up focus group" : "Send"}
                disabled={twins.length === 0}
                footer={twins.length > 0 ? chatFooter : undefined}
                hasAttachments={pendingImages.length > 0 || pendingPdfText !== null}
              />
            ) : (
              <HomeComposer
                value={studyMessage}
                onValueChange={setStudyMessage}
                onSubmit={handleStudySubmit}
                placeholder="What would you like to know?"
                submitLabel="Start study"
                disabled={studyAudiencesLoading || studyAudiences.length === 0}
                reserveFooterSlot={twins.length > 0}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
